import React, { memo } from 'react';
import StudentListPanel from './StudentListPanel';
import TestHistoryPanel from './TestHistoryPanel';
import { Card } from '../../../components/common';
import { Undo2 } from 'lucide-react';
import { useStudents } from '../hooks/useStudents';

const StudentSection = memo(({
  userType,
  navigateToStudent,
  navigateToStudentList,
  onStudentDetailClick,
  onSelectedStudentChange,
  isMobile = false,
  isStudentSelected,
  selectedStudent,
  studentListRef,
  // 테스트 관련 props
  testHistory,
  selectedTest,
  isTestHistoryLoading,
  hasMoreTests,
  onTestSelect,
  testHistoryRef,
  setCurrentMobilePanel,
  loadMoreTests
}) => {
  const {
    studentsData,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    totalItems,
    filters,
    handleFilterChange,
    handleKeyPress,
    handleSearch,
    handleReset,
    // Teacher 데이터 추가
    teachers,
    isTeachersLoading,
    // 전체 목록 복원 함수 추가
    resetToFullList,
  } = useStudents(userType);

  // 학생 목록 스크롤 핸들러 (학생 목록 페이지에서만 활성화)
  const handleStudentListScroll = React.useCallback(() => {
    // 특정 학생이 선택된 상태에서는 학생 목록 스크롤 비활성화
    if (isStudentSelected) return;
    
    if (!studentListRef.current || isLoadingMore) return;
    
    const { scrollTop, scrollHeight, clientHeight } = studentListRef.current;
    
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      loadMore();
    }
  }, [loadMore, isLoadingMore, studentListRef, isStudentSelected]);

  // 선택된 학생 카드 클릭 핸들러
  const handleSelectedStudentCardClick = React.useCallback(() => {
    console.log('학생 목록으로 돌아가기 - 전체 목록 복원 트리거');
    // 먼저 전체 목록 복원을 트리거
    if (resetToFullList) {
      resetToFullList();
    }
    // 그 다음 네비게이션
    navigateToStudentList();
  }, [navigateToStudentList, resetToFullList]);

  // StudentSection은 URL 기반 학생 조회를 담당하지 않음
  // 이 책임은 LearningStatus 컴포넌트에서 처리

  // 학생이 선택되지 않았을 때: 학생 목록 표시
  if (!isStudentSelected || !selectedStudent) {
    return (
      <StudentListPanel
        isStudentSelected={isStudentSelected}
        selectedStudent={selectedStudent}
        studentsData={studentsData}
        totalItems={totalItems}
        filters={filters}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        onStudentSelect={navigateToStudent}
        onSelectedStudentChange={onSelectedStudentChange} // 상태 업데이트용 추가
        onStudentDetailClick={onStudentDetailClick}
        onSelectedStudentCardClick={handleSelectedStudentCardClick}
        onFilterChange={handleFilterChange}
        onFilterKeyPress={handleKeyPress}
        onSearch={handleSearch}
        onReset={handleReset}
        onScroll={handleStudentListScroll}
        studentListRef={studentListRef}
        isMobile={isMobile}
        userType={userType}
        // Teacher 데이터 전달
        teachers={teachers}
        isTeachersLoading={isTeachersLoading}
      />
    );
  }

  // 학생이 선택되었을 때: 선택된 학생 카드 + 테스트 목록 표시
  if (isMobile) {
    return (
      <>
        {/* 선택된 학생 카드 */}
        <div 
          className="relative cursor-pointer rounded-3xl border bg-white p-[14.4px] shadow-lg transition-shadow hover:shadow-xl border-transparent ring-2 ring-primary"
          onClick={handleSelectedStudentCardClick}
          style={{ width: 'calc(100% - 28.8px)', margin: '14.4px auto 7.2px auto' }}
        >
              <div className="grid grid-cols-[6fr_4fr_44px] items-center">
                {/* 좌측 영역 - 학생 정보 */}
                <div className="flex min-w-0 items-center gap-2 pr-2">
                  <div 
                    className="flex shrink-0 items-center justify-center rounded-full text-white font-bold text-sm bg-gradient-to-r from-primary to-primary/70"
                    style={{ width: '32px', height: '32px' }}
                    title={`학생 ID: ${selectedStudent.student_id}`}
                  >
                    {selectedStudent.student_id}
                  </div>
                  
                  {/* 학생 정보 */}
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <h4 className="truncate text-sm font-medium">{selectedStudent.student_name}</h4>
                    <p className="truncate text-xs text-gray-600">{selectedStudent.school_name} · {selectedStudent.school_grade}학년</p>
                    {selectedStudent.student_email && (
                      <p className="truncate text-xs text-gray-500">{selectedStudent.student_email}</p>
                    )}
                  </div>
                </div>
                
                {/* 중앙 영역 - 담당 선생님 & 강의반 정보 */}
                <div className="flex items-center">
                  <div className="flex flex-1 flex-col justify-center text-right">
                    {(selectedStudent.teacher_name || selectedStudent.in_charge_teacher || selectedStudent.lecture) ? (
                      <>
                        {(selectedStudent.teacher_name || selectedStudent.in_charge_teacher) && (
                          <div className="flex items-center justify-end text-xs">
                            <span className="shrink-0 text-gray-500">담당:</span>
                            <span className="ml-1 truncate font-medium text-gray-700" style={{ maxWidth: '80px' }}>
                              {selectedStudent.teacher_name || selectedStudent.in_charge_teacher}
                            </span>
                          </div>
                        )}
                        {selectedStudent.lecture && (
                          <div className="mt-0.5 flex items-center justify-end text-xs">
                            <span className="shrink-0 text-gray-500">강의:</span>
                            <span className="ml-1 truncate font-medium text-gray-700" style={{ maxWidth: '80px' }}>
                              {selectedStudent.lecture}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-xs text-gray-400">-</div>
                    )}
                  </div>
                </div>
                
                {/* 우측 영역 - 빈 공간 (Edit 버튼 자리) */}
                <div></div>
              </div>
              <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-center text-xs text-primary">
                <Undo2 size={16} />
              </div>
        </div>
        
        {/* 테스트 목록 패널 */}
        <TestHistoryPanel
          testHistory={testHistory}
          selectedTest={selectedTest}
          isTestHistoryLoading={isTestHistoryLoading}
          hasMoreTests={hasMoreTests}
          onTestSelect={onTestSelect}
          testHistoryRef={testHistoryRef}
          isMobile={isMobile}
          setCurrentMobilePanel={setCurrentMobilePanel}
          onLoadMore={loadMoreTests}
          selectedStudent={selectedStudent}
        />
      </>
    );
  }

  // 데스크톱: 분리된 레이아웃 (학생 카드 + 테스트 목록을 각각 별도 영역에)
  return (
    <div className="flex h-full flex-col gap-4">
      {/* 선택된 학생 카드 */}
      <div 
        className="relative cursor-pointer rounded-3xl border bg-white p-[14.4px] shadow-lg transition-shadow hover:shadow-xl border-transparent ring-2 ring-primary"
        onClick={handleSelectedStudentCardClick}
      >
        <div className="grid grid-cols-[6fr_4fr_44px] items-center">
          {/* 좌측 영역 - 학생 정보 */}
          <div className="flex min-w-0 items-center gap-2 pr-2">
            <div 
              className="flex shrink-0 items-center justify-center rounded-full text-white font-bold text-sm bg-gradient-to-r from-primary to-primary/70"
              style={{ width: '32px', height: '32px' }}
              title={`학생 ID: ${selectedStudent.student_id}`}
            >
              {selectedStudent.student_id}
            </div>
            
            {/* 학생 정보 */}
            <div className="min-w-0 flex-1 overflow-hidden">
              <h4 className="truncate text-sm font-medium">{selectedStudent.student_name}</h4>
              <p className="truncate text-xs text-gray-600">{selectedStudent.school_name} · {selectedStudent.school_grade}학년</p>
              {selectedStudent.student_email && (
                <p className="truncate text-xs text-gray-500">{selectedStudent.student_email}</p>
              )}
            </div>
          </div>
          
          {/* 중앙 영역 - 담당 선생님 & 강의반 정보 */}
          <div className="flex items-center">
            <div className="flex flex-1 flex-col justify-center text-right">
              {(selectedStudent.teacher_name || selectedStudent.in_charge_teacher || selectedStudent.lecture) ? (
                <>
                  {(selectedStudent.teacher_name || selectedStudent.in_charge_teacher) && (
                    <div className="flex items-center justify-end text-xs">
                      <span className="shrink-0 text-gray-500">담당:</span>
                      <span className="ml-1 truncate font-medium text-gray-700" style={{ maxWidth: '80px' }}>
                        {selectedStudent.teacher_name || selectedStudent.in_charge_teacher}
                      </span>
                    </div>
                  )}
                  {selectedStudent.lecture && (
                    <div className="mt-0.5 flex items-center justify-end text-xs">
                      <span className="shrink-0 text-gray-500">강의:</span>
                      <span className="ml-1 truncate font-medium text-gray-700" style={{ maxWidth: '80px' }}>
                        {selectedStudent.lecture}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-xs text-gray-400">-</div>
              )}
            </div>
          </div>
          
          {/* 우측 영역 - 빈 공간 (Edit 버튼 자리) */}
          <div></div>
        </div>
        <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-center text-xs text-primary">
          <Undo2 size={16} />
        </div>
      </div>
      
      {/* 테스트 목록 패널 */}
      <TestHistoryPanel
        testHistory={testHistory}
        selectedTest={selectedTest}
        isTestHistoryLoading={isTestHistoryLoading}
        hasMoreTests={hasMoreTests}
        onTestSelect={onTestSelect}
        testHistoryRef={testHistoryRef}
        isMobile={isMobile}
        setCurrentMobilePanel={setCurrentMobilePanel}
        onLoadMore={loadMoreTests}
        selectedStudent={selectedStudent}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // 기본 props 비교
  if (
    prevProps.userType !== nextProps.userType ||
    prevProps.isMobile !== nextProps.isMobile ||
    prevProps.isStudentSelected !== nextProps.isStudentSelected ||
    prevProps.selectedStudent?.student_id !== nextProps.selectedStudent?.student_id
  ) {
    return false;
  }

  // 테스트 관련 props - 깊은 비교
  if (
    prevProps.isTestHistoryLoading !== nextProps.isTestHistoryLoading ||
    prevProps.hasMoreTests !== nextProps.hasMoreTests ||
    prevProps.selectedTest?.exam_id !== nextProps.selectedTest?.exam_id
  ) {
    return false;
  }

  // testHistory 배열 비교 - 길이와 첫 번째 요소만 체크
  if (
    prevProps.testHistory?.length !== nextProps.testHistory?.length ||
    prevProps.testHistory?.[0]?.exam_id !== nextProps.testHistory?.[0]?.exam_id
  ) {
    return false;
  }

  // 함수 props는 참조 비교 (이미 useCallback으로 메모이제이션됨)
  return (
    prevProps.navigateToStudent === nextProps.navigateToStudent &&
    prevProps.navigateToStudentList === nextProps.navigateToStudentList &&
    prevProps.onStudentDetailClick === nextProps.onStudentDetailClick &&
    prevProps.onSelectedStudentChange === nextProps.onSelectedStudentChange &&
    prevProps.onTestSelect === nextProps.onTestSelect &&
    prevProps.setCurrentMobilePanel === nextProps.setCurrentMobilePanel
  );
});

StudentSection.displayName = 'StudentSection';

export default React.memo(StudentSection); 